import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";
import { useLocale } from "@/i18n/locale";
import { localePath } from "@/i18n/locale";
import { useT } from "@/i18n/ui";

/**
 * Global CTA button system for the marketing site.
 *
 * Two context-based motion styles (no third style anywhere):
 *  - context="hero"    → prominent lift + fill motion (reference: Creators hero CTAs)
 *  - context="section" → restrained fill/colour transition (reference: Creators second block CTAs)
 *
 * Visual variants keep the primary / secondary / yellow hierarchy and always
 * keep the label contrast-safe through every animation frame.
 */

export type CtaContext = "hero" | "section";
export type CtaBackgroundContext = "default" | "yellow";
export type CtaVariant =
  | "primaryDark"
  | "secondaryLight"
  | "yellow"
  | "outlineLight"
  | "outlineDark"
  | "onYellowPrimary"
  | "onYellowSecondary";

const base =
  "cta-base group relative inline-flex shrink-0 select-none items-center justify-center gap-2 " +
  "overflow-hidden rounded-full border text-sm font-semibold tracking-tight whitespace-nowrap " +
  "outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--brand-black)] " +
  "focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 " +
  "[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:transition-colors";

const sizes = {
  default: "min-h-11 px-6 py-2",
  lg: "min-h-12 px-8 py-2.5 sm:text-[0.95rem]",
  sm: "min-h-10 px-5 text-[0.8125rem]",
} as const;

const variants: Record<CtaVariant, string> = {
  primaryDark:
    "cta-fill border-transparent bg-[var(--brand-black)] text-white [--cta-fill:var(--brand-yellow)] hover:text-[var(--brand-black)]",
  secondaryLight:
    "cta-fill border-[var(--brand-black)]/20 bg-background text-[var(--brand-black)] [--cta-fill:var(--brand-beige)] hover:text-[var(--brand-black)]",
  yellow:
    "cta-fill border-transparent bg-[var(--brand-yellow)] text-[var(--brand-black)] [--cta-fill:var(--brand-black)] hover:text-white",
  outlineLight:
    "cta-fill border-[var(--brand-black)] bg-transparent text-[var(--brand-black)] [--cta-fill:var(--brand-black)] hover:text-white",
  outlineDark:
    "cta-fill border-white bg-transparent text-white [--cta-fill:var(--brand-yellow)] hover:text-[var(--brand-black)]",
  // Yellow / yellow-gradient backgrounds: strict black & white pairing.
  onYellowPrimary:
    "cta-fill border-transparent bg-[var(--brand-black)] text-white [--cta-fill:#ffffff] hover:text-[var(--brand-black)] focus-visible:text-[var(--brand-black)]",
  onYellowSecondary:
    "cta-fill border-[var(--brand-black)]/30 bg-white text-[var(--brand-black)] [--cta-fill:var(--brand-black)] hover:border-[var(--brand-black)] hover:text-white focus-visible:text-white",
};

/**
 * Maps an authored variant onto the approved black/white pairing when the
 * button sits on a yellow or yellow-gradient surface. Primary stays primary.
 */
export function resolveCtaVariant(
  variant: CtaVariant,
  backgroundContext: CtaBackgroundContext = "default",
): CtaVariant {
  if (backgroundContext !== "yellow") return variant;
  return variant === "primaryDark" || variant === "onYellowPrimary"
    ? "onYellowPrimary"
    : "onYellowSecondary";
}

export type CtaButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  context?: CtaContext;
  variant?: CtaVariant;
  backgroundContext?: CtaBackgroundContext;
  size?: keyof typeof sizes;
  asChild?: boolean;
};

export const CtaButton = React.forwardRef<HTMLButtonElement, CtaButtonProps>(
  (
    {
      className,
      context = "section",
      variant = "primaryDark",
      backgroundContext = "default",
      size = "lg",
      asChild = false,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp: React.ElementType = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        data-cta={context}
        className={cn(
          base,
          sizes[size],
          variants[resolveCtaVariant(variant, backgroundContext)],
          context === "hero" ? "cta-hero" : "cta-section",
          className,
        )}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <span className="relative z-[1] inline-flex items-center gap-2">{children}</span>
        )}
      </Comp>
    );
  },
);
CtaButton.displayName = "CtaButton";

/** Anchor-flavoured convenience wrapper used by most page CTAs. */
export function CtaLink({
  href,
  label,
  children,
  className,
  context,
  variant,
  backgroundContext,
  size,
  ...rest
}: {
  href: string;
  label?: string;
  children?: React.ReactNode;
  className?: string;
  context?: CtaContext;
  variant?: CtaVariant;
  backgroundContext?: CtaBackgroundContext;
  size?: keyof typeof sizes;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "className">) {
  const locale = useLocale();
  const t = useT();
  // Internal links keep the active language; hashes and external URLs are kept as-is.
  const resolvedHref = href.startsWith("/") ? localePath(href, locale) : href;
  const content =
    typeof children === "string" ? t(children) : (children ?? (label ? t(label) : null));
  return (
    <CtaButton
      asChild
      context={context}
      variant={variant}
      backgroundContext={backgroundContext}
      size={size}
      className={className}
    >
      <a href={resolvedHref} {...rest}>
        <span className="relative z-[1] inline-flex items-center gap-2">
          {content}
        </span>
      </a>
    </CtaButton>
  );
}
